import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { formatUserDateTime, formatUserDateTimeWithUTC, formatUTCStamp } from '../../utils/dateFormat';
import { getFileTimestamp } from '../../utils/timezone';
import { useGeneralSettings } from '../../hooks/useGeneralSettings';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  ScrollText, Search, RefreshCw, Download, Upload, Loader2, X, History,
  Check, ChevronsUpDown, Archive, Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { csvCell } from '../../utils/csv';
import { AUDIT_ACTION_LABELS, getAuditActionLabel } from '../../utils/auditActions';
import { getAuditActionIcon, SecurityFlagsBadge } from '../../components/shared/AuditLogList';
import { DateRangePresetFilter } from '../../components/shared/DateRangePresetFilter';
import { PaginationFooter } from '../../components/shared/PaginationFooter';
import { useUrlPage, useUrlPageSize, useDebouncedValue, useSkipFirstEffect } from '../../components/shared/useUrlPagination';

const EXPORT_LIMIT = 5000;

const formatChangeValue = (v) => {
  if (v == null || v === '') return 'empty';
  if (Array.isArray(v)) return `${v.length} item(s)`;
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const flattenChanges = (changes) => {
  if (!changes || typeof changes !== 'object') return [];
  return Object.entries(changes)
    .filter(([k]) => k !== 'updated_at' && k !== 'tenant_id' && k !== 'security_flags' && k !== 'login_ip' && k !== 'login_country')
    .map(([field, change]) => {
      const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
      if (change && typeof change === 'object' && ('old' in change || 'new' in change)) {
        return { field: fieldLabel, old: change.old, new: change.new };
      }
      return { field: fieldLabel, value: change };
    });
};

export default function GlobalAuditLog() {
  const { api, user } = useAuth();
  const { isSubpageEnabled } = useFeatureFlags();
  const hasArchives = user?.role === 'super_admin' || (user?.role === 'admin' && isSubpageEnabled('admin_audit_archives'));
  const { dateFormat: userDateFmt, timeFormat: userTimeFmt, timezone: userTimezone } = useGeneralSettings();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [page, setPage] = useUrlPage('page');
  const [rowsPerPage, setRowsPerPage] = useUrlPageSize('global-audit', 25);

  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [dateResetKey, setDateResetKey] = useState(0);
  const [users, setUsers] = useState([]);
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [archivesOpen, setArchivesOpen] = useState(false);
  const [archives, setArchives] = useState([]);
  const [archivesLoading, setArchivesLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState(null);

  const fetchArchives = useCallback(async () => {
    setArchivesLoading(true);
    try {
      const res = await api.get('/admin/audit-archives');
      setArchives(res.data.archives || []);
    } catch (e) {
      toast.error('Failed to load archives');
    } finally {
      setArchivesLoading(false);
    }
  }, [api]);

  const openArchives = () => { setArchivesOpen(true); fetchArchives(); };

  const handleGenerateArchive = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/admin/audit-archives/generate', {});
      if (res.data.created) {
        toast.success(`Archive ${res.data.archive.label} created (${res.data.archive.entries} entries)`);
      } else {
        toast.info(`Archive ${res.data.archive.label} already exists`);
      }
      fetchArchives();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate archive');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadArchive = async (archive, format) => {
    const key = `${archive.id}-${format}`;
    setDownloadingKey(key);
    try {
      const res = await api.get(`/admin/audit-archives/${archive.id}/download?format=${format}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${archive.label}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    } finally {
      setDownloadingKey(null);
    }
  };

  const debouncedSearch = useDebouncedValue(search, 400);

  const fmtDT = useCallback(
    (ts) => formatUserDateTime(ts, userDateFmt, userTimeFmt, userTimezone),
    [userDateFmt, userTimeFmt, userTimezone]
  );
  // Audit policy: always show the exact UTC moment next to the viewer's local time.
  const fmtDTUTC = useCallback(
    (ts) => formatUserDateTimeWithUTC(ts, userDateFmt, userTimeFmt, userTimezone),
    [userDateFmt, userTimeFmt, userTimezone]
  );

  // Server-side filter params shared by the list fetch and CSV export.
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
    if (actionFilter !== 'all') params.set('action', actionFilter);
    if (userFilter !== 'all') params.set('user_id', userFilter);
    if (dateRange.from) params.set('from_date', new Date(dateRange.from).toISOString());
    if (dateRange.to) params.set('to_date', new Date(dateRange.to).toISOString());
    return params;
  }, [debouncedSearch, actionFilter, userFilter, dateRange.from, dateRange.to]);

  const fetchLogs = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    try {
      const params = buildFilterParams();
      params.set('skip', String((page - 1) * rowsPerPage));
      params.set('limit', String(rowsPerPage));
      const res = await api.get(`/audit?${params.toString()}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Failed to load audit logs:', e);
      toast.error('Failed to load audit logs');
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [api, buildFilterParams, page, rowsPerPage]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Reset to page 1 whenever a filter changes (skips mount so ?page= deep-links survive).
  useSkipFirstEffect(() => { setPage(1); }, [debouncedSearch, actionFilter, userFilter, dateRange.from, dateRange.to, rowsPerPage]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/users?limit=2000&include_sub_accounts=true');
        const list = (res.data || []).map((u) => ({ id: u.id, label: u.full_name || u.email || u.id, email: u.email }));
        list.sort((a, b) => a.label.localeCompare(b.label));
        setUsers(list);
      } catch (e) {
        console.error('Failed to load users for filter:', e);
      }
    })();
  }, [api]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = buildFilterParams();
      params.set('skip', '0');
      params.set('limit', String(EXPORT_LIMIT));
      const res = await api.get(`/audit?${params.toString()}`);
      const all = res.data.logs || [];
      // Honour the row selection when non-empty, otherwise export every filtered row.
      const rows = selectedIds.length > 0 ? all.filter((l) => selectedIds.includes(l.id)) : all;
      if (!rows.length) {
        toast.info('No rows to export');
        return;
      }
      const header = ['Timestamp', 'Timestamp (UTC)', 'Action', 'User', 'User Email', 'Performed By', 'IP Address', 'Details', 'Changes'];
      const csv = [header.join(',')].concat(rows.map((l) => [
        csvCell(fmtDT(l.timestamp)),
        csvCell(formatUTCStamp(l.timestamp)),
        csvCell(getAuditActionLabel(l.action)),
        csvCell(l.user_name || ''),
        csvCell(l.user_email || ''),
        csvCell(l.actor_email || 'System'),
        csvCell(l.ip_address || ''),
        csvCell(l.details || ''),
        csvCell(l.changes ? JSON.stringify(l.changes) : ''),
      ].join(','))).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_log_${getFileTimestamp()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      if (selectedIds.length > 0) {
        toast.success(`Exported ${rows.length} selected audit entr${rows.length === 1 ? 'y' : 'ies'}`);
      } else if (total > EXPORT_LIMIT) {
        toast.warning(`Exported the most recent ${rows.length} of ${total} entries (export cap ${EXPORT_LIMIT}). Narrow the date range to export older entries.`);
      } else {
        toast.success(`Exported ${rows.length} audit entries`);
      }
    } catch (e) {
      console.error('Export failed:', e);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const hasActiveFilters = search || actionFilter !== 'all' || userFilter !== 'all' || dateRange.from;

  const clearFilters = () => {
    setSearch('');
    setActionFilter('all');
    setUserFilter('all');
    setDateRange({ from: undefined, to: undefined });
    setDateResetKey((k) => k + 1);
    setPage(1);
  };

  const actionOptions = useMemo(
    () => Object.entries(AUDIT_ACTION_LABELS).sort((a, b) => a[1].localeCompare(b[1])),
    []
  );

  return (
    <div className="space-y-4" data-testid="global-audit-log-panel">
      <div className="hidden lg:flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#2563eb]/10">
          <ScrollText className="w-6 h-6 text-[#2563eb]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground" data-testid="global-audit-log-title">Audit Log</h1>
          <p className="text-sm text-muted-foreground">Unified chronological audit trail across all users</p>
        </div>
      </div>

      <Card data-testid="global-audit-card">
        <CardContent className="p-4 space-y-4">
          {/* Filters toolbar */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Search action, actor, details..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 pl-8 pr-7 text-sm"
                data-testid="global-audit-search"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="global-audit-search-clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-48 h-9 text-sm" data-testid="global-audit-action-filter">
                <SelectValue placeholder="All actions" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All Actions</SelectItem>
                {actionOptions.map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Popover open={userPickerOpen} onOpenChange={setUserPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-52 h-9 justify-between font-normal text-sm"
                  data-testid="global-audit-user-filter"
                >
                  <span className="truncate">
                    {userFilter === 'all' ? 'All Users' : (users.find((u) => u.id === userFilter)?.label || 'All Users')}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[300px]" align="start">
                <Command filter={(value, s) => (value.toLowerCase().includes(s.toLowerCase()) ? 1 : 0)}>
                  <CommandInput placeholder="Search users..." data-testid="global-audit-user-search" />
                  <CommandList className="max-h-64">
                    <CommandEmpty>No user found</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="All Users"
                        onSelect={() => { setUserFilter('all'); setUserPickerOpen(false); }}
                        data-testid="global-audit-user-option-all"
                      >
                        <Check className={`mr-2 h-4 w-4 ${userFilter === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                        All Users
                      </CommandItem>
                      {users.map((u) => (
                        <CommandItem
                          key={u.id}
                          value={`${u.label} ${u.email || ''}`}
                          onSelect={() => { setUserFilter(u.id); setUserPickerOpen(false); }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${userFilter === u.id ? 'opacity-100' : 'opacity-0'}`} />
                          <span className="truncate">{u.label}</span>
                          {u.email && u.email !== u.label && (
                            <span className="ml-1.5 text-xs text-muted-foreground truncate">{u.email}</span>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <DateRangePresetFilter
              value={dateRange}
              onChange={setDateRange}
              resetSignal={dateResetKey}
              testIdPrefix="global-audit-date"
            />
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 text-xs"
                onClick={clearFilters}
                data-testid="global-audit-clear-filters"
              >
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-9" onClick={fetchLogs} disabled={loading} data-testid="global-audit-refresh">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Refresh
              </Button>
              <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={handleExport} disabled={exporting || selectedIds.length === 0} data-testid="global-audit-export">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                <span className="text-xs">{selectedIds.length > 0 ? `Export (${selectedIds.length})` : 'Export'}</span>
              </Button>
              {hasArchives && (
                <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={openArchives} data-testid="global-audit-archives-button">
                  <Archive className="w-4 h-4" />
                  <span className="text-xs">Archives</span>
                </Button>
              )}
            </div>
          </div>

          {/* Selection bar — page checkbox + count + Select All / Clear (same
              pattern as Impersonation Audit; PaginationFooter scrolls here). */}
          <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 overflow-hidden" data-testid="global-audit-selection-bar">
            <Checkbox
              data-testid="global-audit-select-page"
              checked={logs.length > 0 && logs.every((l) => selectedIds.includes(l.id))}
              onCheckedChange={(checked) => {
                const pageIds = logs.map((l) => l.id).filter(Boolean);
                setSelectedIds((prev) => checked
                  ? Array.from(new Set([...prev, ...pageIds]))
                  : prev.filter((id) => !pageIds.includes(id)));
              }}
            />
            <span data-testid="global-audit-selection-count">{selectedIds.length} selected</span>
            <span data-testid="global-audit-count">{total} entr{total === 1 ? 'y' : 'ies'}{hasActiveFilters ? ' (filtered)' : ''}</span>
            {selectedIds.length > 0 && (<>
              <Button
                variant="ghost" size="sm" className="h-6 px-2 text-xs"
                data-testid="global-audit-select-all-toggle"
                disabled={selectAllLoading}
                onClick={async () => {
                  if (selectedIds.length >= total) { setSelectedIds([]); return; }
                  setSelectAllLoading(true);
                  try {
                    const params = buildFilterParams();
                    params.set('ids_only', 'true');
                    const res = await api.get(`/audit?${params.toString()}`);
                    setSelectedIds(Array.isArray(res.data?.ids) ? res.data.ids : []);
                  } catch (e) {
                    toast.error('Failed to select all rows');
                  } finally {
                    setSelectAllLoading(false);
                  }
                }}
              >
                {selectAllLoading ? 'Selecting…' : (selectedIds.length >= total ? 'Deselect All' : 'Select All')}
              </Button>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedIds([])} data-testid="global-audit-selection-clear">Clear</Button>
            </>)}
          </div>

          {/* Log list */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <History className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No audit entries found</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="global-audit-list">
              {logs.map((log) => {
                const { Icon, cls } = getAuditActionIcon(log.action);
                const changes = flattenChanges(log.changes);
                return (
                  <div key={log.id} className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/50" data-testid="global-audit-row">
                    <Checkbox
                      className="mt-2"
                      data-testid={`global-audit-select-${log.id}`}
                      checked={selectedIds.includes(log.id)}
                      onCheckedChange={(checked) => {
                        setSelectedIds((prev) => checked ? [...prev, log.id] : prev.filter((id) => id !== log.id));
                      }}
                    />
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${cls}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <p className="font-medium text-sm">{getAuditActionLabel(log.action)}</p>
                          <SecurityFlagsBadge flags={log.changes?.security_flags} country={log.changes?.login_country} city={log.changes?.login_city} />
                        </div>
                        <Badge variant="outline" className="text-xs flex-shrink-0">{fmtDTUTC(log.timestamp)}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{log.details || '—'}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-xs text-muted-foreground">
                        <span>
                          User: <span className="text-foreground font-medium">{log.user_name || log.user_email || log.user_id?.slice(0, 8) || '—'}</span>
                          {log.user_email && log.user_name ? ` (${log.user_email})` : ''}
                        </span>
                        <span className="break-all">By: {log.actor_email || 'System'}</span>
                        {log.ip_address && <span>IP: {log.ip_address}</span>}
                      </div>
                      {changes.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {changes.map((c, idx) => (
                            <div key={idx} className="text-xs p-1.5 bg-muted rounded flex flex-wrap items-center gap-x-2">
                              <span className="font-medium">{c.field}:</span>
                              {'value' in c ? (
                                <span className="font-mono break-all">{formatChangeValue(c.value)}</span>
                              ) : (
                                <>
                                  <span className="text-muted-foreground line-through break-all">{formatChangeValue(c.old)}</span>
                                  <span>&rarr;</span>
                                  <span className="font-mono break-all">{formatChangeValue(c.new)}</span>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination footer — always mounted so the card height stays
              consistent across loading / empty / populated states. */}
          <PaginationFooter
            page={page}
            setPage={setPage}
            totalItems={total}
            itemsPerPage={rowsPerPage}
            setItemsPerPage={setRowsPerPage}
            testIdPrefix="global-audit"
            disabled={loading}
            alwaysShow
          />
        </CardContent>
      </Card>

      {/* Archives dialog — monthly compliance exports that survive the retention purge */}
      <Dialog open={archivesOpen} onOpenChange={setArchivesOpen}>
        <DialogContent className="max-w-2xl" data-testid="audit-archives-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5" /> Audit Archives
            </DialogTitle>
            <DialogDescription>
              Monthly compliance exports of the audit trail. Archives are kept outside the retention purge.
              Configure the schedule in Global Settings &rarr; Data Retention. Downloads are themselves audited.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={handleGenerateArchive} disabled={generating} data-testid="audit-archives-generate">
              {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
              Archive previous month now
            </Button>
          </div>
          <div className="max-h-[55vh] overflow-y-auto">
            {archivesLoading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
              </div>
            ) : archives.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground" data-testid="audit-archives-empty">
                No archives yet. Enable monthly archiving in Global Settings &rarr; Data Retention, or generate one now.
              </div>
            ) : (
              <div className="space-y-2" data-testid="audit-archives-list">
                {archives.map((a) => (
                  <div key={a.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border" data-testid={`audit-archive-row-${a.label}`}>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{a.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {a.entries.toLocaleString()} entries &middot; created {fmtDT(a.created_at)} &middot; by {a.created_by}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {(a.files || []).map((f) => (
                        <Button
                          key={f.format}
                          size="sm" variant="outline" className="h-8 text-xs gap-1"
                          disabled={downloadingKey === `${a.id}-${f.format}`}
                          onClick={() => handleDownloadArchive(a, f.format)}
                          data-testid={`audit-archive-download-${a.label}-${f.format}`}
                        >
                          {downloadingKey === `${a.id}-${f.format}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                          {f.format.toUpperCase()}
                          <span className="text-muted-foreground">
                            ({f.size > 1048576 ? `${(f.size / 1048576).toFixed(1)} MB` : `${(f.size / 1024).toFixed(1)} KB`})
                          </span>
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
